import { type Vec2, clamp, wrapIndex } from '../core/math';
import { SURFACES, type Surface } from '../physics/constants';
import type { KartState } from '../physics/vehicle';
import { type Track } from './generate';

/** How far from the centerline the barrier stands at `index` on `side`
 *  (+1 left of the racing direction). See `src/track/barrier.ts`. */
export function barrierDistance(track: Track, index: number, side: 1 | -1): number {
  return (side > 0 ? track.barriers.left : track.barriers.right).dist[index];
}

export interface TrackSample {
  /** Nearest centerline index. */
  index: number;
  /** Signed offset from the centerline along its normal. Positive is left. */
  lateral: number;
  surface: Surface;
}

/**
 * How far along the lap a continuity hint is trusted to reach, in samples. A
 * kart at full speed covers well under a third of a metre per 120 Hz tick and
 * the centerline is sampled every 2 m, so the index moves by a fraction of one
 * sample per tick — 32 either way is enormous headroom for the honest case
 * while staying far short of the ~320 samples that separate the two halves of
 * Suzuka's crossover.
 */
const CONTINUITY_WINDOW = 32;

/**
 * Uniform-grid nearest-point lookup over the centerline. The physics tick asks
 * "what am I driving on" 120 times a second, so a linear scan of ~410 points is
 * not acceptable; this narrows it to a handful of candidates.
 *
 * Nearest-point alone is ambiguous wherever a circuit passes close to itself.
 * Suzuka's back straight crosses its front straight — a flyover in reality,
 * flattened here — and leaves the two centerlines 0.57 m apart. A kart on one
 * of them is then routinely *nearer* to the other, and the answer flips to a
 * ribbon 644 m away: wrong lateral, wrong run-off kind, wrong barrier normal,
 * and a stretch of the racing line that reports as off-track. Callers that
 * know where the kart was last tick pass that index as a hint, which resolves
 * the ambiguity the only way it can be resolved — by continuity. See
 * {@link TrackQuery.sample}.
 */
export class TrackQuery {
  private readonly cellSize: number;
  private readonly cols: number;
  private readonly rows: number;
  private readonly originX: number;
  private readonly originY: number;
  private readonly cells: number[][];
  /** Squared distance past which a hinted index is no longer believable — see
   *  {@link TrackQuery.sample}. */
  private readonly continuitySq: number;

  constructor(
    private readonly track: Track,
    cellSize = 16,
  ) {
    this.cellSize = cellSize;
    // A kart is never further from its own centerline than the barrier that
    // clamps it, plus the sample spacing that separates "distance to the
    // nearest sample point" from "perpendicular distance to the line", plus a
    // tick's travel for the frame before `clampToBarrier` has pulled it back.
    const reach = track.width / 2 + track.kerbWidth + track.wallMargin + track.spacing * 2;
    this.continuitySq = reach * reach;
    const pad = 60;
    this.originX = track.bounds.minX - pad;
    this.originY = track.bounds.minY - pad;
    this.cols = Math.ceil((track.bounds.maxX - track.bounds.minX + pad * 2) / cellSize) + 1;
    this.rows = Math.ceil((track.bounds.maxY - track.bounds.minY + pad * 2) / cellSize) + 1;
    this.cells = Array.from({ length: this.cols * this.rows }, () => []);

    track.center.forEach((p, i) => {
      const gx = this.col(p.x);
      const gy = this.row(p.y);
      this.cells[gy * this.cols + gx].push(i);
    });
  }

  private col(x: number): number {
    return clamp(Math.floor((x - this.originX) / this.cellSize), 0, this.cols - 1);
  }

  private row(y: number): number {
    return clamp(Math.floor((y - this.originY) / this.cellSize), 0, this.rows - 1);
  }

  /**
   * What is under `(x, y)`.
   *
   * `hint` is the index this same body resolved to last tick. Where the
   * circuit does not touch itself it changes nothing — the nearest sample is
   * the nearest sample either way — but where two parts of the lap run within
   * a kart's width of each other it is the only thing that can say which of
   * them the kart is actually on. It is believed only while the hinted ribbon
   * is close enough to be underneath the kart at all; a reset, a respawn or a
   * ghost seeking to an arbitrary point leaves it far away, and the global
   * search below then answers exactly as it always did.
   */
  sample(x: number, y: number, hint?: number): TrackSample {
    if (hint !== undefined) {
      const near = this.nearestNear(x, y, hint);
      if (near !== null) {
        const lateral = this.lateralAt(near, x, y);
        return { index: near, lateral, surface: surfaceFor(this.track, lateral, near) };
      }
    }

    const gx = this.col(x);
    const gy = this.row(y);

    let bestIndex = 0;
    let bestDistSq = Infinity;

    // Expand the search ring until something is found, then one ring more so a
    // closer point in a diagonal neighbour cannot be missed.
    const maxRing = Math.max(this.cols, this.rows);
    let foundRing = -1;
    for (let ring = 0; ring <= maxRing; ring++) {
      if (foundRing >= 0 && ring > foundRing + 1) break;
      for (let dy = -ring; dy <= ring; dy++) {
        for (let dx = -ring; dx <= ring; dx++) {
          // Only the perimeter of this ring; the interior was covered already.
          if (ring > 0 && Math.abs(dx) !== ring && Math.abs(dy) !== ring) continue;
          const cx = gx + dx;
          const cy = gy + dy;
          if (cx < 0 || cy < 0 || cx >= this.cols || cy >= this.rows) continue;
          for (const i of this.cells[cy * this.cols + cx]) {
            const p = this.track.center[i];
            const ddx = p.x - x;
            const ddy = p.y - y;
            const d2 = ddx * ddx + ddy * ddy;
            if (d2 < bestDistSq) {
              bestDistSq = d2;
              bestIndex = i;
              if (foundRing < 0) foundRing = ring;
            }
          }
        }
      }
    }

    const lateral = this.lateralAt(bestIndex, x, y);
    return { index: bestIndex, lateral, surface: surfaceFor(this.track, lateral, bestIndex) };
  }

  /**
   * Nearest centerline index within {@link CONTINUITY_WINDOW} of `hint`, or
   * `null` when nothing that close to the hint is also close enough to the
   * point for the kart to plausibly be on it.
   */
  private nearestNear(x: number, y: number, hint: number): number | null {
    const n = this.track.center.length;
    let best = -1;
    let bestDistSq = Infinity;
    for (let d = -CONTINUITY_WINDOW; d <= CONTINUITY_WINDOW; d++) {
      const i = wrapIndex(hint + d, n);
      const p = this.track.center[i];
      const ddx = p.x - x;
      const ddy = p.y - y;
      const d2 = ddx * ddx + ddy * ddy;
      if (d2 < bestDistSq) {
        bestDistSq = d2;
        best = i;
      }
    }
    return best >= 0 && bestDistSq <= this.continuitySq ? best : null;
  }

  /**
   * Project onto the two segments meeting at `index` rather than using the
   * vertex normal directly — at 2 m spacing the vertex-only answer visibly
   * stair-steps, which would make the track edge feel serrated.
   */
  private lateralAt(index: number, x: number, y: number): number {
    const n = this.track.center.length;
    let best = 0;
    let bestDistSq = Infinity;

    for (const step of [-1, 1]) {
      const ia = step === -1 ? wrapIndex(index - 1, n) : index;
      const ib = step === -1 ? index : wrapIndex(index + 1, n);
      const a = this.track.center[ia];
      const b = this.track.center[ib];
      const ex = b.x - a.x;
      const ey = b.y - a.y;
      const lenSq = ex * ex + ey * ey;
      if (lenSq < 1e-9) continue;
      const t = clamp(((x - a.x) * ex + (y - a.y) * ey) / lenSq, 0, 1);
      const px = a.x + ex * t;
      const py = a.y + ey * t;
      const d2 = (x - px) * (x - px) + (y - py) * (y - py);
      if (d2 < bestDistSq) {
        bestDistSq = d2;
        // Sign from the 2D cross product: left of travel is positive.
        const cross = ex * (y - a.y) - ey * (x - a.x);
        best = (cross < 0 ? -1 : 1) * Math.sqrt(d2);
      }
    }
    return best;
  }
}

/**
 * `index` is the nearest centerline sample, used only to look up which kind of
 * run-off a real circuit has authored there (`Track.runoffKind` — see
 * docs/circuit-fidelity.md §6). Omit it, or query a track with no authored
 * run-off, and everything beyond the kerb is grass exactly as before.
 */
export function surfaceFor(track: Track, lateral: number, index?: number): Surface {
  const a = Math.abs(lateral);
  const half = track.width / 2;
  if (a <= half) return SURFACES.track;
  if (a <= half + track.kerbWidth) return SURFACES.kerb;
  if (index !== undefined && track.runoffKind) return SURFACES[track.runoffKind[index]];
  return SURFACES.grass;
}

/**
 * Height (metres) at a centerline index, 0 for a track with no authored
 * elevation. The one place both renderers and the camera should read
 * `Track.height` from, so "flat if unauthored" only has to be written once.
 */
export function heightAt(track: Track, index: number): number {
  return track.height ? track.height[index] : 0;
}

/**
 * Cross-slope contribution at a centerline index: positive tilts the side on
 * the local corner's *inside* down, by `bank * offset` at a point `offset`
 * metres out. "Inside" is derived from the sign of `curvature` at that same
 * index rather than stored separately, so a banking keyframe only has to say
 * how steep, never which way — it always leans toward whichever way the road
 * is actually turning there. Below a small curvature threshold (effectively
 * straight) it returns 0 rather than let sign noise on a near-straight jitter
 * the surface.
 */
function bankTilt(track: Track, index: number): number {
  if (!track.bank) return 0;
  const k = track.curvature[index];
  const side = Math.abs(k) > 0.002 ? Math.sign(k) : 0;
  return track.bank[index] * side;
}

/**
 * Height at a signed lateral offset from the centerline at `index` — `heightAt`
 * plus whatever the local banking adds at that distance out. `lateralOffset`
 * follows `Track.normal`'s own sign convention (positive left of travel), so
 * callers already computing a vertex as `center[index] + normal[index] *
 * offset` pass that same signed `offset` straight through.
 *
 * Every piece of geometry that spans some width at one centerline index —
 * the road surface, the kerbs either side of it, the wall further out, the
 * run-off ribbon beyond that — has to read banking from here rather than
 * `heightAt` alone, or a corner banked steeply enough to be visible (only
 * Zandvoort's two, at this scale — a metre-plus across the road width) shows
 * the kerb and wall sitting flat while the road tilts away underneath them.
 *
 * The tilt itself is only extrapolated out to the kerb's outer edge
 * (`width/2 + kerbWidth`) — a real bowl is banked across the track and its
 * kerbs, not indefinitely further out, so beyond that this holds the kerb
 * edge's own height rather than projecting the same slope all the way to the
 * wall. Without the clamp, Zandvoort's ~15 m-out barrier would tilt several
 * extra metres past what the visible bowl itself does.
 */
export function heightAtLateral(track: Track, index: number, lateralOffset: number): number {
  const bankExtent = track.width / 2 + track.kerbWidth;
  const clamped = Math.max(-bankExtent, Math.min(bankExtent, lateralOffset));
  return heightAt(track, index) - bankTilt(track, index) * clamped;
}

/**
 * Height at an arbitrary world point, for anything positioned off the
 * centerline itself — the kart, the camera, the trail.
 *
 * Projects onto the nearest of the two centerline *segments* around each
 * candidate sample (the same technique `lateralAt` uses for the barrier
 * clamp) and linearly interpolates that segment's two endpoint heights,
 * rather than snapping to whichever sample is nearest. Nearest-sample
 * snapping looks fine on a static mesh, where every vertex sits exactly on a
 * sample, but a query point sweeps continuously between samples 2 m apart
 * (`SPACING`) — the camera trailing a kart down a slope in particular — and
 * on a real gradient a 2 m step in *height* is not a small error. It reads
 * as the ground shaking underfoot, worst exactly where the slope is
 * steepest, which is exactly what a tamed-but-real climb like Spa's still
 * has (docs/circuit-fidelity.md §5).
 */
export function heightAtPoint(track: Track, x: number, y: number): number {
  if (!track.height) return 0;
  const { center, height } = track;
  const n = center.length;
  let bestDistSq = Infinity;
  let bestHeight = 0;

  for (let i = 0; i < n; i++) {
    const a = center[i];
    const j = wrapIndex(i + 1, n);
    const b = center[j];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const lenSq = ex * ex + ey * ey;
    const t = lenSq > 1e-9 ? clamp(((x - a.x) * ex + (y - a.y) * ey) / lenSq, 0, 1) : 0;
    const px = a.x + ex * t;
    const py = a.y + ey * t;
    const dx = x - px;
    const dy = y - py;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestDistSq) {
      bestDistSq = d2;
      bestHeight = height[i] + (height[j] - height[i]) * t;
    }
  }
  return bestHeight;
}

export interface BarrierImpact {
  /** Speed (m/s) with which the kart was travelling into the wall, > 0. */
  impactSpeed: number;
  /** Nearby centerline sample, for FX placement. */
  index: number;
  /** +1 hit the left wall, -1 the right. */
  side: 1 | -1;
  /** 0..1 how head-on the hit was — 1 is perpendicular, 0 a pure graze. */
  headOn: number;
}

/**
 * A hard track boundary. Real circuits have gravel, a tyre wall or fencing a
 * short way past the kerb — running off is not free forever the way grass's
 * flat mu/drag penalty implies, and without this a wide-enough excursion
 * could be driven at any speed with nothing to stop it.
 *
 * Clamps the kart back to the boundary and kills the outward (normal)
 * component of its velocity. The along-track component is kept for a graze —
 * sliding along a wall scrubs speed the way sliding along a wall actually
 * does — but a **head-on** hit also bleeds most of the tangential speed,
 * because hitting a barrier square-on is not a wall-scrape.
 *
 * Returns an `impact` when contact was made this tick (and only then), so the
 * session can apply damage and trigger feedback. `impactSpeed` is the speed
 * removed by the clamp, so a gentle graze against a wall you were already
 * sliding along reports near zero.
 */
export function clampToBarrier(
  track: Track,
  query: TrackQuery,
  kart: KartState,
  hint?: number,
): BarrierImpact | null {
  const sample = query.sample(kart.x, kart.y, hint);
  const sign = sample.lateral >= 0 ? 1 : -1;
  // Where the barrier actually is at this point of the lap, which on a circuit
  // that comes back alongside itself is nearer than the margin it asked for.
  const limit = barrierDistance(track, sample.index, sign);
  const a = Math.abs(sample.lateral);
  if (a <= limit) return null;

  const normal = track.normal[sample.index];
  const overshoot = a - limit;
  kart.x -= normal.x * sign * overshoot;
  kart.y -= normal.y * sign * overshoot;

  const vn = kart.vx * normal.x * sign + kart.vy * normal.y * sign;
  if (vn <= 0) return null;

  // Outward speed we were carrying into the wall.
  const impactSpeed = vn;

  kart.vx -= normal.x * sign * vn;
  kart.vy -= normal.y * sign * vn;

  // How head-on: a perpendicular hit has most of its speed along the normal, a
  // graze has most of it tangential. Where 1 = square-on, 0 = pure scrape.
  const tx = -normal.y * sign;
  const ty = normal.x * sign;
  const vt = kart.vx * tx + kart.vy * ty;
  const headOn = impactSpeed / (impactSpeed + Math.abs(vt));

  // A square-on hit sheds most of the remaining tangential speed the way armco
  // stops a car that hits it flat, while a graze keeps sliding along. Retain
  // only that fraction of the tangential velocity that matches the graze.
  if (headOn > 0.05) {
    const keptVt = vt * (1 - headOn);
    kart.vx = tx * keptVt;
    kart.vy = ty * keptVt;
  }

  return { impactSpeed, index: sample.index, side: sign, headOn };
}

/** The start/finish line as a segment spanning the full track width. */
export function startLine(track: Track): { a: Vec2; b: Vec2 } {
  const c = track.center[track.startIndex];
  const nrm = track.normal[track.startIndex];
  const half = track.width / 2 + track.kerbWidth;
  return {
    a: { x: c.x + nrm.x * half, y: c.y + nrm.y * half },
    b: { x: c.x - nrm.x * half, y: c.y - nrm.y * half },
  };
}

/**
 * Fraction along p0->p1 at which it crosses line a->b, or null. Sub-tick
 * precision matters: at 120 Hz and 25 m/s a whole tick is 21 cm of track, which
 * is more than enough to shuffle a leaderboard.
 */
export function segmentCrossing(p0: Vec2, p1: Vec2, a: Vec2, b: Vec2): number | null {
  const rx = p1.x - p0.x;
  const ry = p1.y - p0.y;
  const sx = b.x - a.x;
  const sy = b.y - a.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((a.x - p0.x) * sy - (a.y - p0.y) * sx) / denom;
  const u = ((a.x - p0.x) * ry - (a.y - p0.y) * rx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return t;
}
